import React, { ReactElement, useEffect, useMemo } from 'react';
import cn from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { useRecoilState } from 'recoil';

import { IconButton, Spinner } from 'components';
import Logo from 'components/Logo';
import { getLayout } from 'components/layouts/Header';
import MatchCard from 'components/candidate/MatchCard';
import { ButtonV2 } from 'components/form';
import { Row } from 'components/layout';
import { signInModalInstance } from 'components/candidate/SignInModal';
import { saveInAppModalInstance } from 'components/candidate/SaveInAppModal';
import { useResponsive } from 'hooks/useResponsive';
import { MatchAttributes, NestedType } from 'lib/models/matches';
import { parseResponse } from 'lib/utils/parser';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { userSecretState } from 'lib/atoms/userSecretAtom';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { APP_LINK, getOpportunityPublicPagePathname } from 'utils/urls';
import { isAndroid } from 'utils/dom';

import styles from './FunnelMatches.module.scss';

const FunnelMatches = (): ReactElement => {
  const router = useRouter();
  const screens = useResponsive();
  const { internshipMatches, jobMatches, eventMatches, matchesMeta } =
    useCandidateMatches();
  const unvisited_count = matchesMeta?.unvisited_count;
  const [userSecret] = useRecoilState(userSecretState);
  const { isCandidate, isTemporaryCandidate } = useAuth();

  const getMatchCard = (touchpoints: Array<MatchAttributes>) => {
    return touchpoints?.map(
      ({
        id,
        title,
        start_date,
        end_date,
        creatable_for,
        posted_at,
        matchable_created_at,
        nested_type,
        minimum_salary,
        maximum_salary,
        pay_currency,
        matchable_slug,
        sanitized_description,
        cities,
        topics,
        icon_url: SocietyCompanyLogo,
        university,
        application_deadline,
      }) => {
        const createdBy = parseResponse<
          CompanyAttributes | StudentSocietyAttributes
        >(creatable_for);
        const { name, icon_url, is_verified } = createdBy || {};
        const getPublicUrl = () => {
          if (
            nested_type !== NestedType.STUDENT_SOCIETY &&
            nested_type !== NestedType.COMPANY
          ) {
            return getOpportunityPublicPagePathname(
              nested_type,
              matchable_slug
            );
          } else if (nested_type === NestedType.STUDENT_SOCIETY) {
            return `${PAGE_ROUTES.SOCIETIES}/${matchable_slug}`;
          } else if (nested_type === NestedType.COMPANY) {
            return `${PAGE_ROUTES.COMPANIES}/${matchable_slug}`;
          } else return '';
        };

        return (
          <MatchCard
            key={id}
            icon_url={icon_url}
            title={title}
            createdBy={name}
            startDate={start_date}
            end_date={end_date}
            created_at={posted_at ? posted_at : matchable_created_at}
            application_deadline={application_deadline}
            is_verified={is_verified}
            nested_type={nested_type}
            maximum_salary={maximum_salary}
            minimum_salary={minimum_salary}
            pay_currency={pay_currency}
            description={sanitized_description}
            className={styles.card}
            url={getPublicUrl()}
            cities={cities}
            topics={topics}
            SocietyCompanyLogo={SocietyCompanyLogo}
            university={university}
          />
        );
      }
    );
  };

  const jobMatchesCard = useMemo(() => getMatchCard(jobMatches), [jobMatches]);
  const internshipMatchesCard = useMemo(
    () => getMatchCard(internshipMatches),
    [internshipMatches]
  );
  const eventMatchesCard = useMemo(
    () => getMatchCard(eventMatches),
    [eventMatches]
  );

  const onAppDownloadClick = () => {
    router.push(APP_LINK);
  };

  const onUseHuzzleClick = async () => {
    try {
      if (isTemporaryCandidate) {
        await saveInAppModalInstance();
      } else if (!isCandidate) {
        await signInModalInstance();
      }
      router.push(PAGE_ROUTES.CANDIDATE_DASHBOARD);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userSecret && isCandidate) {
      router.push(PAGE_ROUTES.CANDIDATE_DASHBOARD);
    }
  }, [userSecret, isCandidate]);

  useEffect(() => {
    if (!userSecret) {
      router.push(PAGE_ROUTES.GET_PERSONALISED_MATCHES);
    }
  }, [userSecret]);

  if (unvisited_count === undefined) {
    return (
      <div className={styles.spinner}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Matches | Huzzle</title>
        <meta name="description" content="Funnel matches - Huzzle" />
      </Head>
      <section className={styles.FunnelMatches} data-testid="FunnelMatches">
        <main className={styles.main}>
          <p className={styles.title}>
            We found <mark>{unvisited_count} matches</mark> for you.
          </p>
          <p className={styles.title}>Check them out! 😊</p>
          {jobMatchesCard}
          {internshipMatchesCard}
          {eventMatchesCard}
        </main>
        <div className={styles.download}>
          {screens.sm ? (
            <p className={styles.downloadTitle}>
              Download the huzzle app to get more matches, more opportunities.
            </p>
          ) : (
            <p className={styles.downloadTitle}>
              Get huzzle now to see more matches, more opportunities.
            </p>
          )}
          {screens.sm ? (
            <IconButton
              iconName={
                isAndroid() ? 'play-store-download' : 'app-store-download'
              }
              size="auto"
              iconClassName={styles.icon}
              onClick={onAppDownloadClick}
              className={styles.buttons}
            />
          ) : (
            <>
              <ButtonV2
                onClick={onUseHuzzleClick}
                width={180}
                className={styles.buttons}
              >
                Use huzzle now
              </ButtonV2>
              <Row
                align="center"
                justify="center"
                isFullWidthRow
                className={styles.option}
              >
                <div className={styles.separator}></div>
                OR
                <div className={styles.separator}></div>
              </Row>
              <Row
                align="center"
                justify="center"
                isFullWidthRow
                className={styles.buttons}
              >
                <IconButton
                  iconName={'app-store-download'}
                  size="auto"
                  iconClassName={styles.icon}
                  disabled
                />
                <IconButton
                  iconName={'play-store-download'}
                  size="auto"
                  iconClassName={cn(styles.icon, styles.android)}
                  disabled
                />
              </Row>
            </>
          )}
          <Logo name="huzzle-new" size="small" color="primary" />
          <p className={styles.desc}>
            Your career, <mark>on autopilot</mark> 😇
          </p>
          <p className={styles.copyright}>
            © Copyright 2021 | All rights reserved.
          </p>
        </div>
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'en', 'common')),
    },
  };
}

// @ts-ignore
FunnelMatches.getLayout = getLayout;

export default FunnelMatches;
